import Joi from 'joi';

import { pricingItemSchema } from '../../../../shared/modules/pricing-items/validators/schemas/pricing-item.schema';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleMaintenanceRecordCategoryEnum } from '../../enums/vehicle-maintenance-record-category.enum';

/**
 * Schema for existing vehicle maintenance record validation.
 */
export const updateVehicleMaintenanceSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-maintenance-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|vehicle-maintenance-id|required',
        'number.base': 'validations|vehicle-maintenance-id|must-be-number',
      }),
    }),

    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-maintenance-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(VehicleMaintenanceRecordCategoryEnum))
        .required()
        .messages({
          'any.required':
            'validations|vehicle-maintenance-record-category|required',
          'any.only':
            'validations|vehicle-maintenance-record-category|invalid-value',
          'string.base':
            'validations|vehicle-maintenance-record-category|must-be-string',
          'string.empty':
            'validations|vehicle-maintenance-record-category|cannot-be-empty',
        }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleMaintenanceRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-maintenance-vehicle-id|required',
          'number.base':
            'validations|vehicle-maintenance-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    rentalAgreementId: Joi.when('recordCategory', {
      is: VehicleMaintenanceRecordCategoryEnum.RENTAL_AGREEMENT,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-rental-agreement-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|vehicle-maintenance-rental-agreement-id|required',
          'number.base':
            'validations|vehicle-maintenance-rental-agreement-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleDamageId: Joi.when('recordCategory', {
      is: VehicleMaintenanceRecordCategoryEnum.VEHICLE_DAMAGE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-damage-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-maintenance-damage-id|required',
          'number.base':
            'validations|vehicle-maintenance-damage-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleRoadWorthyCheckId: Joi.when('recordCategory', {
      is: VehicleMaintenanceRecordCategoryEnum.VEHICLE_ROAD_WORTHY_CHECK,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-road-worthy-check-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|vehicle-maintenance-road-worthy-check-id|required',
          'number.base':
            'validations|vehicle-maintenance-road-worthy-check-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    startsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-starts-at|cannot-be-null',
        }),
        otherwise: Joi.date().required().messages({
          'any.required': 'validations|vehicle-maintenance-starts-at|required',
          'date.base':
            'validations|vehicle-maintenance-starts-at|must-be-a-valid-date',
        }),
      })
      .prefs({ abortEarly: true }),

    endsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-maintenance-ends-at|cannot-be-null',
        }),
        otherwise: Joi.date().greater(Joi.ref('startsAt')).required().messages({
          'any.required': 'validations|vehicle-maintenance-ends-at|required',
          'date.base':
            'validations|vehicle-maintenance-ends-at|must-be-a-valid-date',
          'date.greater':
            'validations|vehicle-maintenance-ends-at|cannot-be-before-starts-at',
        }),
      })
      .prefs({ abortEarly: true }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-maintenance-notes|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-maintenance-external-reference|must-be-string',
    }),

    supplierId: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-maintenance-supplier-id|must-be-number',
    }),

    estimatedCost: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-maintenance-estimated-cost|must-be-number',
    }),

    serviceCenter: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-maintenance-service-center|must-be-string',
    }),

    description: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-maintenance-description|must-be-string',
    }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-maintenance-tags|invalid-value',
    }),

    pricingItems: Joi.array().items(pricingItemSchema()).allow(null),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
